import { createHubThemeLight, isValidThemeColor } from '@/ui/theme';
import { ThemeProvider, type Theme } from '@mui/material/styles';
import React, { createContext, useContext } from 'react';

// Single source of truth for the org's primary color. The app layer feeds it in
// once (see _app), keeping ui/ domain-agnostic — this context only carries a color
// string, not a session. Every CustomThemeProvider in the tree reads it, so the
// org color reaches all new-theme components without per-call-site wiring.
const HubPrimaryColorContext = createContext<string | undefined>(undefined);

export function HubPrimaryColorProvider({
  color,
  children,
}: {
  color?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <HubPrimaryColorContext.Provider value={color}>
      {children}
    </HubPrimaryColorContext.Provider>
  );
}

export function useHubPrimaryColor(): string | undefined {
  return useContext(HubPrimaryColorContext);
}

// Themes are immutable and createTheme is non-trivial, so cache one theme per color
// and share it across the many nested CustomThemeProviders — this keeps renders
// referentially stable and avoids rebuilding the theme on every render. Bounded as a
// small LRU because this module is shared across SSR requests: without a cap, every
// distinct org color would accumulate a full Theme for the life of the server process.
// A single render only uses 1–2 colors, so the cap never evicts mid-render.
const THEME_CACHE_LIMIT = 32;
const BRAND_DEFAULT_KEY = '__brand-default__';
const themeCache = new Map<string, Theme>();

function getHubTheme(primaryColor?: string): Theme {
  // Unparseable colors fall back to the brand default inside createHubThemeLight, so
  // key them under the single brand-default entry — otherwise each distinct bad value
  // would cache an identical theme and needlessly churn the small LRU.
  const validColor = isValidThemeColor(primaryColor) ? primaryColor : undefined;
  const key = validColor ?? BRAND_DEFAULT_KEY;

  const cached = themeCache.get(key);
  if (cached) {
    // Refresh recency: re-insert so the most-recently-used key sorts last.
    themeCache.delete(key);
    themeCache.set(key, cached);
    return cached;
  }

  const theme = createHubThemeLight(validColor);
  themeCache.set(key, theme);
  if (themeCache.size > THEME_CACHE_LIMIT) {
    // Evict the least-recently-used entry (first in insertion order).
    const lruKey = themeCache.keys().next().value;
    if (lruKey !== undefined) themeCache.delete(lruKey);
  }
  return theme;
}

type Settings = {
  /**
   * @deprecated Ignored — the theme color now comes from HubPrimaryColorProvider.
   * Kept only so the existing `initialSettings` call sites compile; remove once
   * they stop passing it.
   */
  themeOverride?: Theme;
};

interface CustomThemeProviderProps {
  children: React.ReactNode;
  /** @deprecated Ignored; see {@link Settings}. Kept for call-site compatibility. */
  initialSettings?: Settings;
}

/**
 * Applies the new (Minimal) hub theme, colored by the org's primary color from
 * {@link HubPrimaryColorProvider}. Nesting is safe: a child provider re-applies the
 * same org-colored theme rather than resetting to the brand default.
 */
export function CustomThemeProvider({
  children,
}: CustomThemeProviderProps): React.ReactElement {
  const theme = getHubTheme(useHubPrimaryColor());

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}
